import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, AlertTriangle, RefreshCcw, ExternalLink, ImageOff, FileSearch,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const decisionStyle = {
  auto_approve:   { label: 'Auto-Approved',   bg: 'bg-[#00FF94]/15', text: 'text-[#00FF94]', border: 'border-[#00FF94]/30' },
  pending_review: { label: 'Pending Review',  bg: 'bg-yellow-500/15', text: 'text-yellow-300', border: 'border-yellow-500/30' },
  auto_reject:    { label: 'Auto-Rejected',   bg: 'bg-red-500/15',   text: 'text-red-400', border: 'border-red-500/30' },
};

function AdminVettingLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decisionFilter, setDecisionFilter] = useState('');
  const [tab, setTab] = useState('log'); // 'log' | 'duplicates'
  const [duplicates, setDuplicates] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = decisionFilter ? `?decision=${decisionFilter}` : '';
      const res = await axios.get(`${API_URL}/api/admin/vetting-log${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error('Failed to load vetting log');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [decisionFilter]);

  const runDuplicateScan = useCallback(async () => {
    try {
      setScanning(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/vetting-duplicates?threshold=0.9`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDuplicates(res.data?.matches || []);
      toast.success(`Scan complete — ${res.data?.matches?.length || 0} pair(s) flagged`);
    } catch {
      toast.error('Duplicate scan failed');
    } finally {
      setScanning(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (s) => {
    if (!s) return '—';
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <ShieldCheck className="w-8 h-8 text-[#00F0FF]" />
              Auto-Vetting Log
            </h1>
            <p className="text-neutral-400 text-sm mt-1">
              Every product upload is auto-vetted: URL safety, image moderation, and listing quality. Decisions reviewed here.
            </p>
          </div>
          <Button
            onClick={tab === 'log' ? load : runDuplicateScan}
            disabled={loading || scanning}
            className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold gap-2"
            data-testid="vetting-refresh-btn"
          >
            <RefreshCcw className={`w-4 h-4 ${loading || scanning ? 'animate-spin' : ''}`} />
            {tab === 'log' ? 'Refresh' : 'Scan Now'}
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-white/10">
          {[
            { id: 'log', label: 'Auto-Review Log' },
            { id: 'duplicates', label: 'Description Duplicates' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${
                tab === t.id ? 'text-[#00F0FF] border-b-2 border-[#00F0FF]' : 'text-neutral-400 hover:text-white'
              }`}
              data-testid={`vetting-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'log' && (
          <>
            {/* Filters */}
            <div className="flex gap-2 mb-4">
              {['', 'auto_approve', 'pending_review', 'auto_reject'].map((d) => (
                <button
                  key={d || 'all'}
                  onClick={() => setDecisionFilter(d)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                    decisionFilter === d
                      ? 'bg-[#00F0FF]/20 text-[#00F0FF] border-[#00F0FF]/50'
                      : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white'
                  }`}
                  data-testid={`vetting-filter-${d || 'all'}`}
                >
                  {d ? decisionStyle[d].label : 'All'}
                </button>
              ))}
            </div>

            {/* Log table */}
            <div className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.04] text-neutral-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Product</th>
                    <th className="text-left px-4 py-3 font-semibold">Decision</th>
                    <th className="text-left px-4 py-3 font-semibold">Score</th>
                    <th className="text-left px-4 py-3 font-semibold">Reason</th>
                    <th className="text-left px-4 py-3 font-semibold">Vetted</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && !loading && (
                    <tr>
                      <td colSpan={5} className="text-center text-neutral-500 py-10">
                        No vetted products yet. New uploads auto-vet on submit.
                      </td>
                    </tr>
                  )}
                  {items.map((p) => {
                    const v = p.vetting_results || {};
                    const style = decisionStyle[v.final_decision] || decisionStyle.pending_review;
                    const score = v.listing_review?.score;
                    const isOpen = expanded === p.product_id;
                    return (
                      <React.Fragment key={p.product_id}>
                        <tr
                          className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer"
                          onClick={() => setExpanded(isOpen ? null : p.product_id)}
                          data-testid={`vetting-row-${p.product_id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {p.images?.[0] ? (
                                <img src={p.images[0]} alt="" className="w-8 h-8 rounded object-cover bg-neutral-800" />
                              ) : (
                                <div className="w-8 h-8 rounded bg-neutral-800 flex items-center justify-center"><ImageOff className="w-4 h-4 text-neutral-600" /></div>
                              )}
                              <span className="font-medium text-white truncate max-w-xs">{p.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${style.bg} ${style.text} ${style.border}`}>
                              {v.final_decision === 'auto_approve' && <ShieldCheck className="w-3 h-3" />}
                              {v.final_decision === 'auto_reject' && <AlertTriangle className="w-3 h-3" />}
                              {v.final_decision === 'pending_review' && <FileSearch className="w-3 h-3" />}
                              {style.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-white font-bold">
                            {typeof score === 'number' ? `${score}` : '—'}
                            <span className="text-neutral-500 text-xs">/100</span>
                          </td>
                          <td className="px-4 py-3 text-neutral-300 text-xs max-w-md truncate">
                            {v.reason || '—'}
                          </td>
                          <td className="px-4 py-3 text-neutral-500 text-xs whitespace-nowrap">
                            {fmtDate(v.vetted_at || p.created_at)}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="border-t border-white/5 bg-black/40">
                            <td colSpan={5} className="px-4 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                <DetailCard title="URL Safety">
                                  <p>Checked: <span className="text-white">{(v.url_safety?.checked || []).length}</span></p>
                                  <p>Flagged: <span className={v.url_safety?.flagged?.length ? 'text-red-400' : 'text-[#00FF94]'}>{(v.url_safety?.flagged || []).length}</span></p>
                                  {v.url_safety?.error && <p className="text-yellow-400 mt-1">{v.url_safety.error}</p>}
                                </DetailCard>
                                <DetailCard title="Image Safety">
                                  {(v.image_safety || []).length === 0 && <p className="text-neutral-500">No images</p>}
                                  {(v.image_safety || []).map((im) => (
                                    <p key={im.url} className="truncate">
                                      <span className={im.score >= 70 ? 'text-red-400' : im.score >= 40 ? 'text-yellow-400' : 'text-[#00FF94]'}>{im.score}</span>
                                      <span className="text-neutral-500 ml-1">{im.reason || ''}</span>
                                    </p>
                                  ))}
                                </DetailCard>
                                <DetailCard title="Listing Review">
                                  <p className="text-neutral-300">{v.listing_review?.summary}</p>
                                  {(v.listing_review?.concerns || []).map((c, i) => (
                                    <p key={`${p.product_id}-c-${i}`} className="text-yellow-400">• {c}</p>
                                  ))}
                                </DetailCard>
                              </div>
                              {v.duplicate_image_of && (
                                <div className="mt-3 px-3 py-2 rounded bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs">
                                  ⚠ Duplicate image of <b>{v.duplicate_image_of.name}</b>
                                </div>
                              )}
                              <div className="mt-3 flex gap-2 text-xs">
                                <a href={`/product/${p.product_id}`} target="_blank" rel="noreferrer"
                                   className="inline-flex items-center gap-1 text-[#00F0FF] hover:underline">
                                  View product <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'duplicates' && (
          <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
            <p className="text-neutral-400 text-sm mb-4">
              Click <b>Scan Now</b> above to find products with &gt;= 90% description similarity across <i>different</i> creators. (Same scan also runs nightly at 6:30 AM PT and emails admin a digest.)
            </p>
            <div className="space-y-3">
              {duplicates.length === 0 && (
                <p className="text-center text-neutral-500 py-8 text-sm">No duplicates found yet — run a scan to check.</p>
              )}
              {duplicates.map((m) => (
                <div key={`${m.a.product_id}-${m.b.product_id}`} className="flex items-center gap-4 p-3 rounded-lg bg-white/5">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white truncate">{m.a.name}</p>
                    <p className="text-xs text-neutral-500">by {m.a.creator_id}</p>
                  </div>
                  <div className="text-2xl font-black text-[#00FF94] px-3">{Math.round(m.score * 100)}%</div>
                  <div className="flex-1 min-w-0 text-right">
                    <p className="font-semibold text-white truncate">{m.b.name}</p>
                    <p className="text-xs text-neutral-500">by {m.b.creator_id}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const DetailCard = ({ title, children }) => (
  <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
    <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-2">{title}</p>
    <div className="space-y-1 text-neutral-300">{children}</div>
  </div>
);

export default AdminVettingLog;
